/* --------------------------------------------------------------------
   Compound assistants: 0008 (+33 ⅓ %), 0009 (+20 %),
   0029 (+20 %, assistant-anaesthetist) and emergency 0011.
   ------------------------------------------------------------------ */
import { AdjustedLine, ModifierContext } from '../modifier-models';
import { BaseRule } from '../rule-base';

export class RuleAssistantsCompound extends BaseRule {
    name = 'compound assistants (0008/0009/0029)';

    /* percentage look-up – use 0.3333 not ⅓ to preserve cents rounding */
    private readonly pct: Record<string, number> = { '0008': 0.333, '0009': 0.20, '0029': 0.20 };

    private calculateOperatingTime(line: AdjustedLine): number {
        if (!line.startTime || !line.endTime) return 0;
        
        const start = new Date(`1970-01-01T${line.startTime}`);
        const end = new Date(`1970-01-01T${line.endTime}`);
        return Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60)));
    }

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        if (!line.isModifier || line.isConsult) return;

        const minUnits = ctx.assistantMinUnits ?? 36;

        /* 0008 / 0009 / 0029  – percentage of total fee  */
        if (this.pct[line.code] !== undefined) {
            const pct = this.pct[line.code];

            // Preferred path: calculate from the sum of non-modifier line
            // totals so 0008/0009/0029 all behave consistently and match the
            // clinical intent (a % of the procedure total, not of whatever
            // line happens to precede the modifier).
            if (ctx.totalPrice) {
                const rawRand = +(ctx.totalPrice * pct).toFixed(2);
                const rawUnits = ctx.pricePerUnit
                    ? +(rawRand / ctx.pricePerUnit).toFixed(2)
                    : 0;

                if (autoApply) {
                    line.adjustedRand = rawRand;
                    line.adjustedUnits = line.code === '0009'
                        ? Math.max(rawUnits, minUnits)
                        : rawUnits;
                    line.trace.push(
                        `${line.code}: ${(pct * 100).toFixed(1)}% of total price (${ctx.totalPrice})` +
                        (line.code === '0009' && rawUnits < minUnits ? ` → min ${minUnits}u` : '')
                    );
                } else {
                    line.suggestions = line.suggestions || [];
                    line.suggestions.push({
                        ruleCode: line.code,
                        message: `${(pct * 100).toFixed(1)}% of total price (${ctx.totalPrice})`,
                        kind: 'overwrite',
                        unitsDelta: line.code === '0009' ? Math.max(rawUnits, minUnits) : rawUnits,
                        randDelta: rawRand
                    });
                }
                return;
            }

            // Fallback: no totalPrice in context — preserve the legacy
            // prevUnits-based calc so manual-edit code-paths still produce
            // something sensible.
            const prevUnits = Number(line.meta?.['prevUnits'] ?? 0);
            const raw = +(prevUnits * pct).toFixed(2);

            if (autoApply) {
                line.adjustedUnits = line.code === '0009' ? Math.max(raw, minUnits) : raw;
                if (ctx.pricePerUnit) {
                    line.adjustedRand = +(line.adjustedUnits * ctx.pricePerUnit).toFixed(2);
                }
                line.trace.push(
                    `${line.code}: ${(pct * 100).toFixed(1)} % of ${prevUnits}` +
                    (line.code === '0009' && raw < minUnits ? ` → min ${minUnits}` : '')
                );
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: line.code,
                    message: `${(pct * 100).toFixed(1)}% of ${prevUnits} units`,
                    kind: 'overwrite',
                    unitsDelta: line.code === '0009' ? Math.max(raw, minUnits) : raw
                });
            }
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext) {
        const minUnits = ctx.assistantMinUnits ?? 36;
        let prevUnits = 0;                                 // running base

        lines.forEach(l => {
            if (!l.isModifier && !l.isConsult) {
                prevUnits = l.adjustedUnits ?? l.units ?? 0;   // remember last base
                return;
            }

            /* 0008 / 0009 / 0029  – percentage of total fee  */
            if (this.pct[l.code] !== undefined) {
                const pct = this.pct[l.code];

                // Preferred path: calculate from the sum of non-modifier line
                // totals so 0008/0009/0029 all behave consistently and match
                // the clinical intent (a % of the procedure total).
                if (ctx.totalPrice) {
                    const rawRand = +(ctx.totalPrice * pct).toFixed(2);
                    const rawUnits = ctx.pricePerUnit
                        ? +(rawRand / ctx.pricePerUnit).toFixed(2)
                        : 0;

                    l.adjustedRand = rawRand;
                    l.adjustedUnits = l.code === '0009'
                        ? Math.max(rawUnits, minUnits)
                        : rawUnits;
                    l.trace.push(
                        `${l.code}: ${(pct * 100).toFixed(1)}% of total price (${ctx.totalPrice})` +
                        (l.code === '0009' && rawUnits < minUnits ? ` → min ${minUnits}u` : '')
                    );
                    return;
                }

                // Fallback: no totalPrice — legacy prevUnits-based calc.
                const raw = +(prevUnits * pct).toFixed(2);
                l.adjustedUnits = l.code === '0009'             // 0009 carries the min-rule
                    ? Math.max(raw, minUnits)
                    : raw;
                if (ctx.pricePerUnit) {
                    l.adjustedRand = +(l.adjustedUnits * ctx.pricePerUnit).toFixed(2);
                }
                l.trace.push(
                    `${l.code}: ${(pct * 100).toFixed(1)} % of ${prevUnits}` +
                    (l.code === '0009' && raw < minUnits ? ` → min ${minUnits}` : '')
                );
            }
        });

        return lines;
    }
}